import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { PRODUCT_TYPES } from '@src/constants';

type ProductTypeContextProps = {
  productType: PRODUCT_TYPES;
  productTabSelected: PRODUCT_TYPES | null;
  setProductType: (
    value: React.SetStateAction<keyof typeof PRODUCT_TYPES>,
  ) => void;
  setProductTabSelected: (
    value: React.SetStateAction<keyof typeof PRODUCT_TYPES>,
  ) => void;
  removeProductType: () => void;
};

const ProductTypeContext = createContext<ProductTypeContextProps>(null!);
ProductTypeContext.displayName = 'ProductTypeContext';

const useProductTypeContext = () => {
  const context = useContext(ProductTypeContext);
  /* istanbul ignore next */
  if (!context) {
    throw new Error(
      'You need a ProductTypeProvider component to expose ProductTypeContext',
    );
  }

  return context;
};

const PRODUCT_TYPE = 'product_type';

const ProductTypeContextProvider: React.FC = ({ children }) => {
  const initialState = () => {
    const productTypeFromStorage = window?.localStorage.getItem(
      PRODUCT_TYPE,
    ) as PRODUCT_TYPES;

    if (productTypeFromStorage) {
      return productTypeFromStorage;
    }

    return PRODUCT_TYPES.shortlist; // default, Dashboard and Briefing Form can override it
  };

  const [productType, setProductType] = useState(initialState);
  const [productTabSelected, setProductTabSelected] =
    useState<PRODUCT_TYPES | null>(null);

  const removeProductType = () => {
    window?.localStorage.removeItem(PRODUCT_TYPE);
    setProductType(null);
  };

  useEffect(() => {
    if (productType) {
      window?.localStorage.setItem(PRODUCT_TYPE, productType);
    }
  }, [productType]);

  const value = useMemo(
    () => ({
      productType,
      productTabSelected,
      setProductType,
      setProductTabSelected,
      removeProductType,
    }),
    [setProductType, removeProductType, productType],
  );

  return (
    <ProductTypeContext.Provider value={value}>
      {children}
    </ProductTypeContext.Provider>
  );
};

export { ProductTypeContextProvider, useProductTypeContext, PRODUCT_TYPE };
